// src/pages/DeviceTypeManagement/tabs/ServicesTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Avatar
} from '@mui/material';
import {
  Apps as ServicesIcon,
  Star as DefaultIcon,
  Extension as OptionalIcon
} from '@mui/icons-material';
import { servicesApi } from '../../../services/apiClient';

const ServicesTab = ({ deviceType, onChange }) => {
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get device type's service lists
  const defaultServices = deviceType.defaultServices || [];
  const supportedOptionalServices = deviceType.supportedOptionalServices || [];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await servicesApi.getAll();

      if (response.data.success) {
        setAllServices(response.data.data || []);
      } else {
        setError('Failed to load services');
      }
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err.response?.data?.error || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleDefaultServiceToggle = (serviceId) => {
    const isCurrentlyDefault = defaultServices.includes(serviceId);
    let updatedDefaultServices;
    let updatedOptionalServices = [...supportedOptionalServices];

    if (isCurrentlyDefault) {
      // Remove from default
      updatedDefaultServices = defaultServices.filter(id => id !== serviceId);
    } else {
      // Add to default (and remove from optional if present)
      updatedDefaultServices = [...defaultServices, serviceId];
      updatedOptionalServices = supportedOptionalServices.filter(id => id !== serviceId);
    }

    onChange({
      defaultServices: updatedDefaultServices,
      supportedOptionalServices: updatedOptionalServices
    });
  };

  const handleOptionalServiceToggle = (serviceId) => {
    const isCurrentlyOptional = supportedOptionalServices.includes(serviceId);
    let updatedOptionalServices;
    let updatedDefaultServices = [...defaultServices];

    if (isCurrentlyOptional) {
      // Remove from optional
      updatedOptionalServices = supportedOptionalServices.filter(id => id !== serviceId);
    } else {
      // Add to optional (and remove from default if present)
      updatedOptionalServices = [...supportedOptionalServices, serviceId];
      updatedDefaultServices = defaultServices.filter(id => id !== serviceId);
    }

    onChange({
      defaultServices: updatedDefaultServices,
      supportedOptionalServices: updatedOptionalServices
    });
  };

  const isDefaultService = (serviceId) => defaultServices.includes(serviceId);
  const isOptionalService = (serviceId) => supportedOptionalServices.includes(serviceId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Device Type Services
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" sx={{ mb: 3 }}>
        Configure which services are available for this device type. Default services are automatically enabled for new devices, while optional services can be enabled by users.
      </Alert>

      {allServices.length === 0 ? (
        <Alert severity="warning">
          No services are currently available in the system.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {allServices.map((service) => {
            const isDefault = isDefaultService(service.id);
            const isOptional = isOptionalService(service.id);

            return (
              <Grid item xs={12} key={service.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: isDefault ? 'success.main' : isOptional ? 'info.main' : 'divider',
                    bgcolor: isDefault ? 'success.lighter' : isOptional ? 'info.lighter' : 'background.paper'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {service.logo ? (
                        <Avatar src={service.logo} sx={{ width: 48, height: 48 }}>
                          <ServicesIcon />
                        </Avatar>
                      ) : (
                        <Avatar sx={{ width: 48, height: 48 }}>
                          <ServicesIcon />
                        </Avatar>
                      )}

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" fontWeight="500">
                            {service.friendlyName || service.name}
                          </Typography>
                          <Chip
                            label={service.name}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                          />
                        </Box>
                        {service.description && (
                          <Typography variant="body2" color="text.secondary">
                            {service.description}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isDefault}
                              onChange={() => handleDefaultServiceToggle(service.id)}
                              color="success"
                              icon={<DefaultIcon color="disabled" />}
                              checkedIcon={<DefaultIcon />}
                            />
                          }
                          label={
                            <Typography variant="caption" color={isDefault ? 'success.main' : 'text.secondary'}>
                              Default
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isOptional}
                              onChange={() => handleOptionalServiceToggle(service.id)}
                              color="info"
                              icon={<OptionalIcon color="disabled" />}
                              checkedIcon={<OptionalIcon />}
                            />
                          }
                          label={
                            <Typography variant="caption" color={isOptional ? 'info.main' : 'text.secondary'}>
                              Optional
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<DefaultIcon />}
            label={`${defaultServices.length} Default Services`}
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<OptionalIcon />}
            label={`${supportedOptionalServices.length} Optional Services`}
            color="info"
            variant="outlined"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ServicesTab;
